import { Variants } from "motion/react";

// ========================================
// SPRING CONFIGS - Premium citric springs
// ========================================
export const springConfig = {
  modal: { type: "spring" as const, stiffness: 280, damping: 24, mass: 0.8 },
  gentle: { type: "spring" as const, stiffness: 240, damping: 22, mass: 0.9 },
  snappy: { type: "spring" as const, stiffness: 480, damping: 32, mass: 0.6 },
  bounce: { type: "spring" as const, stiffness: 350, damping: 12, mass: 0.8 },
  smooth: { type: "spring" as const, stiffness: 180, damping: 20, mass: 1 },
  quick: { type: "spring" as const, stiffness: 550, damping: 28, mass: 0.5 },
  citric: { type: "spring" as const, stiffness: 420, damping: 18, mass: 0.7 },
  elastic: { type: "spring" as const, stiffness: 300, damping: 8, mass: 0.6 },
};

// ========================================
// MODAL ANIMATIONS
// ========================================
export const modalOverlayVariants: Variants = {
  hidden: { opacity: 0, backdropFilter: "blur(0px)" },
  visible: {
    opacity: 1,
    backdropFilter: "blur(12px)",
    transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    opacity: 0,
    backdropFilter: "blur(0px)",
    transition: { duration: 0.2, ease: [0.4, 0, 1, 1] },
  },
};

export const modalContentVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.92,
    y: 30,
    filter: "blur(4px)",
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: "blur(0px)",
    transition: springConfig.modal,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    filter: "blur(2px)",
    transition: { duration: 0.18, ease: [0.4, 0, 1, 1] },
  },
};

// ========================================
// LIST ITEM ANIMATIONS (Stagger)
// ========================================
export const listContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.035,
      delayChildren: 0.06,
    },
  },
};

export const listItemVariants: Variants = {
  hidden: { opacity: 0, x: -12, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: springConfig.quick,
  },
};

// ========================================
// HOVER/TAP ANIMATIONS
// ========================================
export const hoverTapVariants = {
  hover: { scale: 1.02, y: -2, transition: { duration: 0.2 } },
  tap: { scale: 0.97, transition: { duration: 0.1 } },
};

export const buttonHoverVariants = {
  hover: { scale: 1.025, transition: springConfig.snappy },
  tap: { scale: 0.965, transition: { duration: 0.08 } },
};

export const subtleHoverVariants = {
  hover: { scale: 1.01, y: -1, transition: { duration: 0.2 } },
  tap: { scale: 0.99, transition: { duration: 0.1 } },
};

// ========================================
// TOAST ANIMATIONS
// ========================================
export const toastVariants: Variants = {
  hidden: { opacity: 0, x: 120, scale: 0.85, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: springConfig.citric,
  },
  exit: {
    opacity: 0,
    x: 120,
    scale: 0.9,
    filter: "blur(2px)",
    transition: { duration: 0.25, ease: [0.4, 0, 1, 1] },
  },
};

// ========================================
// PAGE TRANSITION ANIMATIONS
// ========================================
export const fadeInVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
  },
};

export const slideUpVariants: Variants = {
  hidden: { opacity: 0, y: 16, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: springConfig.gentle,
  },
};

export const slideDownVariants: Variants = {
  hidden: { opacity: 0, y: -16, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: springConfig.gentle,
  },
};

// ========================================
// SIDEBAR ANIMATIONS
// ========================================
export const sidebarItemVariants: Variants = {
  hidden: { opacity: 0, x: -10, filter: "blur(1px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: springConfig.quick,
  },
  hover: { x: 4, transition: { duration: 0.15 } },
};

export const sidebarContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.12,
    },
  },
};

// ========================================
// FORM FIELD ANIMATIONS (Stagger)
// ========================================
export const formContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.12,
    },
  },
};

export const formFieldVariants: Variants = {
  hidden: { opacity: 0, x: -12, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: springConfig.gentle,
  },
};

// ========================================
// ERROR/ALERT ANIMATIONS
// ========================================
export const errorVariants: Variants = {
  hidden: { opacity: 0, y: -12, height: 0, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    y: 0,
    height: "auto",
    filter: "blur(0px)",
    transition: springConfig.gentle,
  },
  exit: {
    opacity: 0,
    y: -12,
    height: 0,
    filter: "blur(2px)",
    transition: { duration: 0.18 },
  },
};

// ========================================
// DROPDOWN ANIMATIONS
// ========================================
export const dropdownVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.93,
    y: -8,
    filter: "blur(3px)",
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: "blur(0px)",
    transition: springConfig.snappy,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -6,
    filter: "blur(2px)",
    transition: { duration: 0.12 },
  },
};

// ========================================
// FLOAT ANIMATION
// ========================================
export const floatAnimation = {
  y: [0, -12, 0],
  transition: {
    repeat: Infinity,
    duration: 2,
    ease: "easeInOut" as const,
  },
};

// ========================================
// PULSE ANIMATION
// ========================================
export const pulseAnimation = {
  scale: [1, 1.06, 1],
  transition: {
    repeat: Infinity,
    duration: 2.5,
    ease: "easeInOut" as const,
  },
};

// ========================================
// CITRIC GLOW PULSE
// ========================================
export const citricGlowAnimation = {
  boxShadow: [
    "0 0 0 0 rgba(132, 204, 22, 0)",
    "0 0 20px 4px rgba(132, 204, 22, 0.2)",
    "0 0 0 0 rgba(132, 204, 22, 0)",
  ],
  transition: {
    repeat: Infinity,
    duration: 2.5,
    ease: "easeInOut" as const,
  },
};

// ========================================
// PANEL SLIDE VARIANTS (View transitions)
// ========================================
export const panelSlideVariants: Variants = {
  hidden: { opacity: 0, x: 24, filter: "blur(3px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: springConfig.smooth,
  },
  exit: {
    opacity: 0,
    x: -24,
    filter: "blur(2px)",
    transition: { duration: 0.18 },
  },
};

// ========================================
// EMAIL ITEM VARIANTS (Inbox list stagger)
// ========================================
export const emailListContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.025,
      delayChildren: 0.03,
    },
  },
};

export const emailItemVariants: Variants = {
  hidden: { opacity: 0, x: -14, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: springConfig.quick,
  },
};

// ========================================
// EMAIL DETAIL VARIANTS (MessageView entrance)
// ========================================
export const emailDetailVariants: Variants = {
  hidden: { opacity: 0, x: 36, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    x: 0,
    filter: "blur(0px)",
    transition: springConfig.smooth,
  },
  exit: {
    opacity: 0,
    x: 36,
    filter: "blur(3px)",
    transition: { duration: 0.18 },
  },
};

// ========================================
// ATTACHMENT VARIANTS
// ========================================
export const attachmentContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.12,
    },
  },
};

export const attachmentItemVariants: Variants = {
  hidden: { opacity: 0, y: 10, scale: 0.94, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: "blur(0px)",
    transition: springConfig.gentle,
  },
};

// ========================================
// THREAD ITEM VARIANTS
// ========================================
export const threadContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.15,
    },
  },
};

export const threadItemVariants: Variants = {
  hidden: { opacity: 0, x: -10, y: 5 },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: springConfig.gentle,
  },
};

// ========================================
// NOTIFICATION VARIANTS
// ========================================
export const notificationDropdownVariants: Variants = {
  hidden: { opacity: 0, scale: 0.93, y: -10, filter: "blur(3px)" },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: "blur(0px)",
    transition: springConfig.snappy,
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: -10,
    filter: "blur(2px)",
    transition: { duration: 0.14 },
  },
};

export const notificationItemVariants: Variants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig.quick,
  },
};

// ========================================
// CALENDAR VARIANTS
// ========================================
export const calendarDayVariants: Variants = {
  hidden: { opacity: 0, scale: 0.88 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springConfig.snappy,
  },
};

export const calendarEventVariants: Variants = {
  hidden: { opacity: 0, scale: 0.82, y: 5 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: springConfig.citric,
  },
};

export const calendarMonthTransition: Variants = {
  enter: { opacity: 0, x: 50 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 },
};

// ========================================
// SKELETON / LOADING VARIANTS
// ========================================
export const skeletonContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

export const skeletonItemVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 0.6,
    transition: { duration: 0.35 },
  },
};

// ========================================
// BADGE VARIANTS (Pop effect)
// ========================================
export const badgeVariants: Variants = {
  hidden: { opacity: 0, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: springConfig.elastic,
  },
  exit: {
    opacity: 0,
    scale: 0,
    transition: { duration: 0.12 },
  },
};

// ========================================
// BANNER VARIANTS (Offline/sync)
// ========================================
export const bannerVariants: Variants = {
  hidden: { opacity: 0, y: -40, height: 0 },
  visible: {
    opacity: 1,
    y: 0,
    height: 40,
    transition: springConfig.gentle,
  },
  exit: {
    opacity: 0,
    y: -40,
    height: 0,
    transition: { duration: 0.22 },
  },
};

// ========================================
// COMPOSE FIELD VARIANTS
// ========================================
export const composeContainerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04,
      delayChildren: 0.1,
    },
  },
};

export const composeFieldVariants: Variants = {
  hidden: { opacity: 0, y: 10, filter: "blur(1px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: springConfig.gentle,
  },
};

// ========================================
// CHIP VARIANTS (email chip add/remove)
// ========================================
export const chipVariants: Variants = {
  hidden: { opacity: 0, scale: 0.75, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: springConfig.citric,
  },
  exit: {
    opacity: 0,
    scale: 0.75,
    filter: "blur(2px)",
    transition: { duration: 0.14 },
  },
};

// ========================================
// TAB INDICATOR VARIANTS
// ========================================
export const tabContentVariants: Variants = {
  hidden: { opacity: 0, y: 10, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: springConfig.gentle,
  },
  exit: {
    opacity: 0,
    y: -10,
    filter: "blur(2px)",
    transition: { duration: 0.16 },
  },
};

// ========================================
// EMPTY STATE VARIANTS
// ========================================
export const emptyStateVariants: Variants = {
  hidden: { opacity: 0, y: 24, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.5,
      staggerChildren: 0.12,
      delayChildren: 0.25,
    },
  },
};

export const emptyStateChildVariants: Variants = {
  hidden: { opacity: 0, y: 12, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: springConfig.gentle,
  },
};
